<template>
  <div class="modal" :class="{ 'is-active': showPreview }">
    <div class="modal-background" @click="$emit('close')"></div>
    <div class="modal-content">
      <div class="box p-6">
        <!-- Draft Watermark -->
        <div class="watermark">DRAFT</div>
        <div class="header">
          <!-- Dynamically choose the logo -->
          <img :src="logo" alt="Account Logo" class="sender-logo">
          <h2>{{ invoice.invoice_type_description }}</h2>
        </div>
        <hr class="separator">
        <div class="buyer-info">
          <div class="left is-size-7 supplier-data">
            <p v-if="invoice.sender" class="div-title"><strong>SUPPLIER</strong></p>
            <p>{{ invoice.sender.name }}</p>
            <p>{{ invoice.sender.address1 }}</p>
            <p v-if="invoice.sender.address2">{{ invoice.sender.address2 }}</p>
            <p v-if="invoice.sender.address3">{{ invoice.sender.address3 }}</p>
            <p>{{ invoice.sender.zipcode }}, {{ invoice.sender.city }}</p>
            <p>{{ invoice.sender.state_display }}, {{ invoice.sender.country_display }}</p>
            <p><strong>Phone:</strong> {{ invoice.sender.contact_number }}</p>
            <p><strong>Email:</strong> {{ invoice.sender.email }}</p>
            <p><strong>Tin:</strong> {{ invoice.sender.tin_no }}</p>
            <p>
              <strong>Identity ({{ invoice.sender.identity_type }}):</strong> {{ invoice.sender.identity_number }}
            </p>
            <p><strong>MSIC Code:</strong> {{ invoice.sender.msic_code }}</p>
            <p><strong>Description:</strong> {{ invoice.sender.business_description }}</p>
          </div>
          <div class="identifiers">
            <div class="right is-size-7 column is-full">
              <p><strong>Document No:</strong> {{ invoice.invoice_number }}</p>
              <p>
                <strong>Invoice Date:</strong> {{ invoice.invoice_date }} {{ invoice.invoice_time }}
              </p>
              <p><strong>Currency:</strong> {{ invoice.invoice_currency }}</p>
              <div v-if="invoice.invoice_type !== 'INVOICE' && invoice.invoice_type !== 'SELF BILLED INVOICE'">
                <p><strong>Seller Org Inv No:</strong> {{ invoice.original_inv_number }}</p>
                <p>
                  <strong>LHDN Org Inv UUID:</strong> {{ invoice.original_inv_ref_number }}
                </p>
              </div>
              <p><strong>Contract Number:</strong> {{ invoice.contract_number }}</p>
              <p><strong>Agent Number:</strong> {{ invoice.agent_number }}</p>
            </div>
          </div>
        </div>
        <hr class="separator">
        <div class="buyer-info">
          <div class="left is-size-7 buyer-data">
            <p v-if="customer" class="div-title"><strong>BUYER</strong></p>
            <p>{{ customer.name }}</p>
            <p>{{ customer.address1 }}</p>
            <p v-if="customer.address2">{{ customer.address2 }}</p>
            <p v-if="customer.address3">{{ customer.address3 }}</p>
            <p>{{ customer.zipcode }}, {{ customer.city }}</p>
            <p>{{ customer.state_display }}, {{ customer.country_display }}</p>
            <p><strong>Phone:</strong> {{ customer.contact_number }}</p>
            <p><strong>Email:</strong> {{ customer.email }}</p>
            <p><strong>Tin:</strong> {{ customer.tin_no }}</p>
            <p>
              <strong>Identity ({{ customer.identity_type }}):</strong> {{ customer.identity_number }}
            </p>
          </div>
        </div>
        
        <hr class="separator">
        <table class="line-items">
          <thead>
            <tr class="is-size-7">
              <th>Classification Code</th>
              <th>Description</th>
              <th>Quantity</th>
              <th>Unit Price</th>
              <th>Amount</th>
              <th>Discount</th>
              <th>Tax Amount</th>
              <th>Line Total</th>
            </tr>
          </thead>
          <tbody class="is-size-7">
            <tr v-for="item in invoice.items" :key="item.id">
              <td>{{ item.classification }}</td>
              <td>{{ item.item_description }}</td>
              <td>{{ item.quantity }}</td>
              <td>{{ formatCurrency(item.unit_price) }}</td>
              <td>{{ formatCurrency(item.subtotal) }}</td>
              <td>{{ item.discount_rate }} %</td>
              <td>{{ formatCurrency(item.tax_amount) }}</td>
              <td>{{ formatCurrency(item.total_excluding_tax) }}</td>
            </tr>
          </tbody>
        </table>
        <div class="totals is-size-7">
          <p><strong>Subtotal:</strong> {{ formatCurrency(invoice.total_excluding_tax) }}</p>
          <p>
            <strong>Discount amount:</strong> {{ formatCurrency(invoice.total_discount_value) }}
          </p>
          <p>
            <strong>Total Exc Tax:</strong> {{ formatCurrency(invoice.total_net_amount) }}
          </p>
          <p><strong>Tax Amount:</strong> {{ formatCurrency(invoice.total_tax_amount) }}</p>
          <p><strong>Rounding Adj. Amount:</strong> {{ formatCurrency(invoice.rounding_amount) }}</p>
          <p>
            <strong>Total Inc Tax:</strong> {{ formatCurrency(invoice.total_including_tax) }}
          </p>
          <p><strong>Prepayment:</strong> {{ formatCurrency(invoice.prepayment_amount) }}</p>
        </div>
        <hr class="separator">
        <!-- Aggregated Final Totals Table -->
        <table class="final-totals">
          <thead>
            <tr class="is-size-7">
              <th>Total Product/Service Price</th>
              <th>Tax Code</th>
              <th>Tax Type</th>
              <th>Tax Rate</th>
              <th>Tax Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(group, index) in aggregatedTotals" :key="index">
              <td class="is-size-7">{{ formatCurrency(group.total_product_price) }}</td>
              <td class="is-size-7">{{ group.tax_code }}</td>
              <td class="is-size-7">{{ group.tax_type }}</td>
              <td class="is-size-7">{{ group.tax_rate }} %</td>
              <td class="is-size-7">{{ formatCurrency(group.tax_amount) }}</td>
            </tr>
          </tbody>
        </table>
        <!-- New Frequency of Billing Information Table -->
        <table class="frequency-billing">
          <thead>
            <tr class="is-size-7">
              <th>Frequency of Billing</th>
              <th>Billing Period Start Date</th>
              <th>Billing Period End Date</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="is-size-7">{{ invoice.frequency_of_billing }}</td>
              <td class="is-size-7">{{ invoice.billing_start }}</td>
              <td class="is-size-7">{{ invoice.billing_end }}</td>
            </tr>
          </tbody>
        </table>
        <!-- Bottom Separator & Additional Remarks -->
        <hr class="separator">
        <div class="additional-remarks is-size-7">
          <strong>Additional Remarks:</strong> {{ invoice.document_remarks }}
        </div>
      </div>
    </div>
    <button class="modal-close is-large" aria-label="close" @click="$emit('close')"></button>
  </div>
</template>

<script>
export default {
  name: 'InvoicePreview',
  props: {
    showPreview: Boolean,
    logo: String,
    invoice: {
      type: Object,
      default: () => ({})
    },
    customer: {
      type: Object,
      default: () => ({})
    },
    lhdn_validation: Object,
    qr_code_lhdn_url: String,
    qr_code_uuid_url: String
  },
  data() {
    return {
      taxTypeMapping: {
        '01': 'Sales Tax',
        '02': 'Service Tax',
        '03': 'Tourism Tax',
        '04': 'High-Value Goods Tax',
        '05': 'Sales Tax on Low Value Goods',
        '06': 'Not Applicable',
        'E': 'Tax exemption (where applicable)'
      }
    };
  },
  computed: {
    computedLogo() {
      return this.invoice.selfbilling ? this.invoice.customer.image : this.invoice.sender.image;
    },
    aggregatedTotals() {
      const groups = {};
      (this.invoice.items || []).forEach(item => {
        const key = `${item.tax_type}-${item.tax_rate}`;
        if (!groups[key]) {
          groups[key] = {
            total_product_price: 0,
            tax_code: item.tax_type,
            tax_type: this.taxTypeMapping[item.tax_type] || item.tax_type,
            tax_rate: item.tax_rate,
            tax_amount: 0
          };
        }
        groups[key].total_product_price += parseFloat(item.subtotal) || 0;
        groups[key].tax_amount += parseFloat(item.tax_amount) || 0;
      });
      return Object.values(groups);
    }
  },
  methods: {
    formatCurrency(value) {
      if (!value) return '0.00';
      return new Intl.NumberFormat('en-MY', { style: 'currency', currency: this.invoice.invoice_currency }).format(value);
    }
  }
};
</script>

<style scoped>
.modal-content {
  width: 900px !important;
  margin: auto;
  position: relative;
}

.watermark {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-45deg);
  font-size: 10em;
  color: rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  z-index: 1;
  pointer-events: none;
}

.box {
  position: relative;
  z-index: 2;
  background-color: white;
}

.modal {
  z-index: 1050;
}

.header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 0;
  margin: 0;
}

h2 {
  font-size: 1.8em;
  text-align: right;
}

.div-title {
  font-weight: bold;
  margin-bottom: 5px;
}

/* Capitalize supplier and buyer data */
.supplier-data,
.buyer-data {
  text-transform: capitalize;
}

.buyer-info,
.identifiers {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  padding: 10px 0;
}
.buyer-info .left,
.identifiers .left {
  padding-left: 10px;
}
.buyer-info .right,
.identifiers .right {
  text-align: left;
  padding-right: 10px;
}

.line-items {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}
.line-items th,
.line-items td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}
.line-items th {
  background-color: #f2f2f2;
  font-weight: bold;
}
.totals {
  text-align: right;
  margin-top: 30px;
  margin-bottom: 30px;
  padding-right: 10px;
}
.final-totals {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}
.final-totals th,
.final-totals td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}
.final-totals th {
  background-color: #f2f2f2;
  font-weight: bold;
}
.separator {
  border-top: 2px solid #ddd;
  margin: 20px 0;
}

.sender-logo {
  max-height: 110px;
  max-width: 100%;
  height: auto;
  width: auto;
  object-fit: contain;
  object-position: left;
}

.summary {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}
.summary .left {
  width: 20%;
}
.summary .right {
  width: 80%;
}
.qr-code {
  width: 100px;
  height: 100px;
}
.is-small {
  font-size: 0.8em;
}
.has-text-danger {
  color: red;
}

/* Frequency Billing Table */
.frequency-billing {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}
.frequency-billing th,
.frequency-billing td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}
.frequency-billing th {
  background-color: #f2f2f2;
  font-weight: bold;
}

/* Additional Remarks styling */
.additional-remarks {
  margin-top: 20px;
  font-size: 0.9em;
}
</style>
